<script lang="ts" module>
  import type { CheckoutActions } from '@athena-lib/Checkout/utils';
  import type { CustomStickersBundleProps } from '@athena-lib/CustomStickersBundle';

  export type OrderTotalProps = {
    subTotal: string;
    shipping?: string;
    taxes: string;
    savings?: string;
    bundleSavings?: CustomStickersBundleProps;
    discountCodes?: CheckoutState['discountCodes'];
    giftCards?: Array<{
      amountFormatted: string;
      remainingFormatted?: string;
      code: string;
    }>;
    removeGiftCard?: CheckoutActions['removeCode'];
    total: string;
    numOfItems: number;
    isExport?: boolean;
  };
</script>

<script lang="ts">
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { Info } from '@athena-lib/Icons';
  import StackDivider from '@athena-lib/Stack/StackDivider.svelte';
  import { Text } from '@athena-lib/Text';
  import type { CheckoutState } from '@stickerapp-org/checkout-sdk';
  import { interpolate } from '@athena-lib/Trans/Trans.svelte';

  const {
    subTotal,
    shipping,
    taxes,
    savings,
    bundleSavings,
    total,
    numOfItems,
    discountCodes,
    giftCards,
    removeGiftCard,
    isExport,
  }: OrderTotalProps = $props();
  const translations = getCommonTranslations();
  const numberOfItems = $derived(`${numOfItems} ${translations.items}`);

  let withVat = $state(true);
  const toggleVat = () => {
    withVat = !withVat;
  };

  const { vatLabel, totalAmount } = $derived.by(() => {
    if (withVat) {
      return {
        vatLabel: translations.vatIncl,
        totalAmount: total,
      };
    }

    // TODO: use the appropriate value for the total excl VAT
    return {
      vatLabel: translations.vatExcl,
      totalAmount: total,
    };
  });
</script>

<div class="flex w-full flex-col">
  <div class="flex justify-between">
    <Text element="span" size="small">{translations.subTotal} ({numberOfItems})</Text>
    <Text element="span" size="small">{subTotal}</Text>
  </div>
  {#if shipping}
    <div class="flex justify-between">
      <Text element="span" size="small">{translations.shipping}</Text>
      <Text element="span" size="small">{shipping}</Text>
    </div>
  {/if}
  <div class="flex justify-between">
    <Text element="span" size="small">{translations.taxes}</Text>
    <Text element="span" size="small">{taxes}</Text>
  </div>

  {#if savings}
    <div class="flex justify-between">
      <Text element="span" size="small" color="magenta">{translations.savings}</Text>
      <Text element="span" size="small" color="magenta">{savings}</Text>
    </div>
  {/if}
  {#if bundleSavings}
    <div class="flex justify-between">
      <Text element="span" size="small" color="magenta"
        >{interpolate(translations.bundleSavings, { amount: bundleSavings.units.toString() })}</Text
      >
      <Text element="span" size="small" color="magenta">{bundleSavings.formattedDiscount}</Text>
    </div>
  {/if}
  {#each discountCodes ?? [] as discountCode}
    <div class="flex justify-between">
      <Text element="span" size="small" color="magenta">{discountCode.code}</Text>
    </div>
  {/each}
  {#each giftCards ?? [] as giftCard}
    <div class="flex justify-between">
      <div class="flex items-center gap-2">
        <Text element="span" size="small" color="magenta">{giftCard.code}</Text>
        {#if removeGiftCard}
          <button type="button" onclick={() => removeGiftCard(giftCard.code)}>
            <Text element="span" size="small" class="underline">{translations.remove}</Text>
          </button>
        {/if}
      </div>
      <Text element="span" size="small" color="magenta"
        >{giftCard.amountFormatted}
        {#if giftCard.remainingFormatted}({giftCard.remainingFormatted} {translations.remaining})
        {/if}</Text
      >
    </div>
  {/each}
  <div class="mt-4">
    <StackDivider dividers="default" />
    <div class="my-2 flex items-end justify-between">
      <span>
        <Text element="span" size="medium">{translations.total}</Text>
        <button type="button" onclick={toggleVat}
          ><Text element="span" size="xsmall" class="ml-2 underline">{vatLabel}</Text></button
        >
      </span>
      <Text element="span" size="medium" bold>{totalAmount}</Text>
    </div>
    <StackDivider dividers="default" />
    {#if isExport}
      <Text element="span" size="xsmall" color="medium" class="my-3 flex items-center gap-2">
        <Info sizeClass="size-4" />
        {translations.additionDutiesAndTaxes}</Text
      >
    {/if}
  </div>
</div>
